import React, { useState } from "react";
import { motion } from "framer-motion";

const LawyerPersonalInfo = ({ userData, setUserData }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: userData.name,
        email: userData.email,
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
        contactNumber: userData.firmDetails?.contactNumber || "",
        address: userData.firmDetails?.address || "",
        licenseNumber: userData.firmDetails?.licenseNumber || "",
    });
    const [profilePicture, setProfilePicture] = useState(userData.profilePicture);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleProfilePictureChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setProfilePicture(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        // Update user data
        setUserData(prev => ({
            ...prev,
            name: formData.name,
            email: formData.email,
            profilePicture: profilePicture
        }));
        setIsEditing(false);
        // Reset password fields
        setFormData(prev => ({
            ...prev,
            currentPassword: "",
            newPassword: "",
            confirmPassword: ""
        }));
    };

    const handleCancel = () => {
        setFormData({
            name: userData.name,
            email: userData.email,
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        });
        setProfilePicture(userData.profilePicture);
        setIsEditing(false);
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Personal Information</h2>
                    <p className="text-gray-600 mt-1">Manage your account details and profile settings</p>
                </div>
                {!isEditing && (
                    <motion.button
                        onClick={() => setIsEditing(true)}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Edit Profile
                    </motion.button>
                )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Profile Picture */}
                <div className="flex items-center space-x-6">
                    <div className="relative">
                        <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                            {profilePicture ? (
                                <img
                                    src={profilePicture}
                                    alt="Profile"
                                    className="w-24 h-24 rounded-full object-cover"
                                />
                            ) : (
                                <span className="text-3xl text-blue-600">
                                    {formData.name.charAt(0).toUpperCase()}
                                </span>
                            )}
                        </div>
                        <label className="absolute bottom-0 right-0 bg-blue-600 text-white rounded-full p-2 cursor-pointer hover:bg-blue-700 transition-colors">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleProfilePictureChange}
                                className="hidden"
                            />
                        </label>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Profile Picture</h3>
                        <p className="text-gray-600 text-sm">Click the camera icon to upload a new photo</p>
                    </div>
                </div>

                {/* Name Field */}
                <div className="w-full">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                    </label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                            ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            : "border-gray-200 bg-gray-50"
                            }`}
                        placeholder="Enter your full name"
                    />
                </div>

                {/* Email Field */}
                <div className="w-full">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                    </label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        disabled={!isEditing}
                        className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                            ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            : "border-gray-200 bg-gray-50"
                            }`}
                        placeholder="Enter your email address"
                    />
                </div>

                {/* Firm Details Section (for Law Firms) */}
                {userData.isLawFirm && (
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Firm Details</h3>

                        {/* Contact Number */}
                        <div className="w-full mb-6">
                            <label htmlFor="contactNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                Contact Number *
                            </label>
                            <input
                                type="tel"
                                id="contactNumber"
                                name="contactNumber"
                                value={formData.contactNumber}
                                onChange={handleInputChange}
                                disabled={!isEditing}
                                className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                                    ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    : "border-gray-200 bg-gray-50"
                                    }`}
                                placeholder="Enter contact number"
                            />
                        </div>

                        {/* Address */}
                        <div className="w-full mb-6">
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                                Firm Address *
                            </label>
                            <textarea
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                disabled={!isEditing}
                                rows={3}
                                className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                                    ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    : "border-gray-200 bg-gray-50"
                                    }`}
                                placeholder="Enter firm address"
                            />
                        </div>

                        {/* License Number */}
                        <div className="w-full mb-6">
                            <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                Bar License Number *
                            </label>
                            <input
                                type="text"
                                id="licenseNumber"
                                name="licenseNumber"
                                value={formData.licenseNumber}
                                onChange={handleInputChange}
                                disabled={!isEditing}
                                className={`w-full px-4 py-3 border rounded-lg transition-all duration-200 ${isEditing
                                    ? "border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    : "border-gray-200 bg-gray-50"
                                    }`}
                                placeholder="Enter bar license number"
                            />
                        </div>

                        {/* Firm Specialties */}
                        <div className="w-full mb-6">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Practice Areas
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {userData.firmDetails?.specialties?.map((specialty, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                                    >
                                        {specialty}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Password Section */}
                {isEditing && (
                    <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Password</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                    Current Password
                                </label>
                                <input
                                    type="password"
                                    id="currentPassword"
                                    name="currentPassword"
                                    value={formData.currentPassword}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Enter current password"
                                />
                            </div>
                            <div>
                                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                    New Password
                                </label>
                                <input
                                    type="password"
                                    id="newPassword"
                                    name="newPassword"
                                    value={formData.newPassword}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Enter new password"
                                />
                            </div>
                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                    Confirm Password
                                </label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                    placeholder="Confirm new password"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Action Buttons */}
                {isEditing && (
                    <div className="flex justify-end space-x-4 pt-6 border-t">
                        <motion.button
                            type="button"
                            onClick={handleCancel}
                            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-200"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Cancel
                        </motion.button>
                        <motion.button
                            type="submit"
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            Save Changes
                        </motion.button>
                    </div>
                )}
            </form>

            {/* Account Information */}
            <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Account Type</h4>
                        <p className="text-gray-600">{userData.isLawFirm ? "Law Firm" : "Individual"}</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Member Since</h4>
                        <p className="text-gray-600">January 2024</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Last Login</h4>
                        <p className="text-gray-600">Today at 2:30 PM</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2">Account Status</h4>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Active
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LawyerPersonalInfo;
